import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router';
import { Icon, type IconName } from './icons';

/*
 * The dashboard's component kit. Everything is built on the tokens in index.css (bg, surface, fg,
 * muted, border, accent and the status colours), so light and dark are both covered by default.
 * Controls are 10px-rounded, cards 18px, hero panels 24px; the brand gradient is reserved for the
 * primary action, selected states and progress.
 */

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ---- buttons ---------------------------------------------------------------------------------

type Variant = 'primary' | 'secondary' | 'soft' | 'ghost' | 'danger';
type Size = 'xs' | 'sm' | 'md' | 'lg';

const BUTTON: Record<Variant, string> = {
  primary: 'bg-brand-gradient text-white shadow-brand hover:brightness-110 active:brightness-95 disabled:opacity-50 disabled:shadow-none',
  secondary:
    'bg-surface text-fg ring-1 ring-inset ring-border-strong shadow-[0_1px_2px_rgba(23,20,31,0.04)] hover:bg-surface-2 active:bg-muted disabled:text-muted-fg disabled:shadow-none dark:bg-white/5 dark:hover:bg-white/8',
  soft: 'bg-accent-soft text-accent hover:bg-accent/15 active:bg-accent/20 disabled:opacity-50',
  ghost: 'text-muted-fg hover:bg-muted hover:text-fg active:bg-border disabled:opacity-50',
  danger: 'bg-destructive text-white hover:brightness-110 active:brightness-95 disabled:opacity-50',
};

const SIZE: Record<Size, string> = {
  xs: 'h-7 gap-1.5 rounded-[8px] px-2.5 text-xs',
  sm: 'h-8 gap-1.5 rounded-[9px] px-3 text-xs',
  md: 'h-10 gap-2 rounded-sm px-4 text-sm',
  lg: 'h-12 gap-2 rounded-md px-6 text-[15px]',
};

const ICON_SIZE: Record<Size, string> = { xs: 'h-3.5 w-3.5', sm: 'h-4 w-4', md: 'h-[18px] w-[18px]', lg: 'h-5 w-5' };

export function Button({
  variant = 'secondary',
  size = 'md',
  loading,
  icon,
  iconRight,
  to,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; loading?: boolean; icon?: IconName; iconRight?: IconName; to?: string }) {
  const classes = cx(
    'inline-flex shrink-0 items-center justify-center whitespace-nowrap font-semibold transition-[background-color,box-shadow,filter,color] duration-150 disabled:cursor-not-allowed',
    BUTTON[variant],
    SIZE[size],
    className,
  );
  const inner = (
    <>
      {loading ? <Spinner className={cx(ICON_SIZE[size], variant === 'primary' || variant === 'danger' ? 'text-white' : 'text-accent')} /> : icon && <Icon name={icon} className={ICON_SIZE[size]} />}
      {children}
      {iconRight && !loading && <Icon name={iconRight} className={ICON_SIZE[size]} />}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={classes} aria-disabled={rest.disabled || undefined}>
        {inner}
      </Link>
    );
  }
  return (
    <button type={rest.type ?? 'button'} {...rest} className={classes} disabled={loading || rest.disabled}>
      {inner}
    </button>
  );
}

export function IconButton({
  label,
  icon,
  size = 'md',
  variant = 'ghost',
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; icon: IconName; size?: 'sm' | 'md' | 'lg'; variant?: 'ghost' | 'secondary' | 'soft' }) {
  const sizes = { sm: 'h-8 w-8 rounded-[9px]', md: 'h-9 w-9 rounded-sm', lg: 'h-10 w-10 rounded-sm' };
  return (
    <button type="button" aria-label={label} title={label} {...rest} className={cx('inline-flex shrink-0 items-center justify-center transition duration-150 disabled:cursor-not-allowed', BUTTON[variant], sizes[size], className)}>
      <Icon name={icon} className={size === 'sm' ? 'h-4 w-4' : 'h-[18px] w-[18px]'} />
    </button>
  );
}

export function Spinner({ className = 'h-5 w-5 text-accent' }: { className?: string }) {
  return (
    <svg className={cx('animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-20" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/** A centred spinner for a page or card still loading. */
export function Loading({ className }: { className?: string }) {
  return (
    <div className={cx('flex items-center justify-center py-16', className)} role="status" aria-label="Loading">
      <Spinner className="h-6 w-6 text-accent" />
    </div>
  );
}

// ---- badges and chips ------------------------------------------------------------------------

export type Tone = 'slate' | 'blue' | 'amber' | 'green' | 'red' | 'violet' | 'indigo' | 'accent';

const TONES: Record<Tone, { badge: string; dot: string }> = {
  slate: { badge: 'bg-muted text-muted-fg ring-border-strong', dot: 'bg-faint' },
  blue: { badge: 'bg-info-soft text-info ring-info/25', dot: 'bg-info' },
  amber: { badge: 'bg-warning-soft text-warning ring-warning/25', dot: 'bg-warning' },
  green: { badge: 'bg-success-soft text-success ring-success/25', dot: 'bg-success' },
  red: { badge: 'bg-destructive-soft text-destructive ring-destructive/25', dot: 'bg-destructive' },
  violet: { badge: 'bg-purple/10 text-purple ring-purple/25 dark:text-accent', dot: 'bg-purple dark:bg-accent' },
  indigo: { badge: 'bg-accent-soft text-accent ring-accent/25', dot: 'bg-accent' },
  accent: { badge: 'bg-brand-gradient text-white ring-transparent', dot: 'bg-white' },
};

export function Badge({ tone, dot, size = 'sm', children, className }: { tone: Tone; dot?: boolean; size?: 'xs' | 'sm'; children: ReactNode; className?: string }) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium ring-1 ring-inset',
        size === 'xs' ? 'px-1.5 py-px text-[10px]' : 'px-2 py-0.5 text-xs',
        TONES[tone].badge,
        className,
      )}
    >
      {dot && <span className={cx('h-1.5 w-1.5 rounded-full', TONES[tone].dot)} aria-hidden />}
      {children}
    </span>
  );
}

/** A toggle pill: languages, competencies a task shows, filters. */
export function Chip({ on, onClick, children, size = 'md', className, disabled, title }: { on: boolean; onClick: () => void; children: ReactNode; size?: 'sm' | 'md'; className?: string; disabled?: boolean; title?: string }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={cx(
        'inline-flex items-center gap-1.5 rounded-full font-medium ring-1 ring-inset transition duration-150 disabled:opacity-50',
        size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3.5 py-1.5 text-sm',
        on ? 'bg-fg text-bg ring-fg shadow-sm' : 'bg-surface text-muted-fg ring-border-strong hover:text-fg hover:ring-fg/40 dark:bg-white/5',
        className,
      )}
    >
      {on && <Icon name="check" className={size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'} strokeWidth={2.5} />}
      {children}
    </button>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded-[5px] bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-fg ring-1 ring-inset ring-border-strong">{children}</kbd>;
}

// ---- surfaces --------------------------------------------------------------------------------

export function Card({
  children,
  className,
  title,
  description,
  actions,
  padding = 'md',
  tone = 'default',
  id,
}: {
  children: ReactNode;
  className?: string;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  padding?: 'none' | 'sm' | 'md';
  tone?: 'default' | 'night' | 'soft';
  id?: string;
}) {
  const pad = padding === 'none' ? '' : padding === 'sm' ? 'p-4' : 'p-5 sm:p-6';
  return (
    <section
      id={id}
      className={cx(
        'overflow-hidden rounded-lg',
        tone === 'night' ? 'bg-night text-white ring-1 ring-white/10' : tone === 'soft' ? 'bg-surface-2 ring-1 ring-border' : 'surface',
        className,
      )}
    >
      {(title || actions || description) && (
        <header className={cx('flex flex-wrap items-start justify-between gap-3 px-5 pt-5 sm:px-6', description ? 'pb-1' : 'pb-0')}>
          <div className="min-w-0">
            {title && <h2 className={cx('text-[15px] font-semibold tracking-tight', tone === 'night' ? 'text-white' : 'text-fg')}>{title}</h2>}
            {description && <p className={cx('mt-0.5 text-sm', tone === 'night' ? 'text-white/60' : 'text-muted-fg')}>{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={cx(pad, Boolean(title || actions || description) && padding !== 'none' && 'pt-4')}>{children}</div>
    </section>
  );
}

/** A titled block on a long page (the job editor): numbered, with a short line on what it is for. */
export function Section({ id, n, title, description, actions, children, className }: { id: string; n?: number; title: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section id={id} className={cx('surface scroll-mt-24 rounded-lg', className)}>
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4 sm:px-6">
        <div className="flex min-w-0 items-start gap-3">
          {n !== undefined && <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-soft font-mono text-[11px] font-semibold text-accent">{n}</span>}
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold tracking-tight text-fg">{title}</h2>
            {description && <p className="mt-0.5 text-sm text-muted-fg">{description}</p>}
          </div>
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </header>
      <div className="p-5 sm:p-6">{children}</div>
    </section>
  );
}

export function Callout({ tone = 'info', title, children, action, icon, className }: { tone?: 'info' | 'warning' | 'success' | 'danger' | 'brand'; title?: ReactNode; children?: ReactNode; action?: ReactNode; icon?: IconName; className?: string }) {
  const tones = {
    info: 'bg-info-soft text-info ring-info/20',
    warning: 'bg-warning-soft text-warning ring-warning/20',
    success: 'bg-success-soft text-success ring-success/20',
    danger: 'bg-destructive-soft text-destructive ring-destructive/20',
    brand: 'bg-accent-soft text-accent ring-accent/20',
  };
  const icons: Record<typeof tone, IconName> = { info: 'info', warning: 'alert', success: 'check', danger: 'alert', brand: 'sparkles' };
  return (
    <div className={cx('flex items-start gap-3 rounded-md px-4 py-3 ring-1 ring-inset', tones[tone], className)} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon name={icon ?? icons[tone]} className="mt-0.5 h-4 w-4" />
      <div className="min-w-0 flex-1 text-sm">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cx(Boolean(title) && 'mt-0.5', 'text-fg-2 dark:text-fg/80')}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function ErrorNote({ error, className }: { error: unknown; className?: string }) {
  if (!error) return null;
  return (
    <Callout tone="danger" className={className}>
      {error instanceof Error ? error.message : String(error)}
    </Callout>
  );
}

export function Empty({ icon = 'spark', title, children, action, className }: { icon?: IconName; title: string; children?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cx('flex flex-col items-center justify-center rounded-lg border border-dashed border-border-strong bg-surface-2 px-6 py-14 text-center', className)}>
      <span className="relative mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-surface text-accent shadow-card ring-1 ring-border">
        <span className="absolute inset-0 rounded-full bg-brand-gradient opacity-10" />
        <Icon name={icon} className="h-6 w-6" />
      </span>
      <p className="display text-xl font-medium text-fg">{title}</p>
      {children && <div className="mt-1.5 max-w-md text-sm text-muted-fg">{children}</div>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('skeleton', className)} aria-hidden />;
}

export function SkeletonCard({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cx('surface rounded-lg p-5', className)} aria-hidden>
      <Skeleton className="h-3 w-24" />
      <Skeleton className="mt-4 h-8 w-32" />
      {Array.from({ length: lines - 1 }).map((_, i) => (
        <Skeleton key={i} className={cx('mt-3 h-3', i % 2 ? 'w-2/3' : 'w-5/6')} />
      ))}
    </div>
  );
}

// ---- forms -----------------------------------------------------------------------------------

export function Field({
  label,
  hint,
  error,
  optional,
  children,
  className,
  as = 'label',
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  optional?: boolean;
  children: ReactNode;
  className?: string;
  /** `div` when the control is not a single input (chips, option cards). */
  as?: 'label' | 'div';
}) {
  const Tag = as;
  return (
    <Tag className={cx('block min-w-0', className)}>
      <span className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className="text-[13px] font-semibold text-fg">{label}</span>
        {optional && <span className="text-[11px] font-medium text-faint">Optional</span>}
      </span>
      {children}
      {hint && !error && <span className="mt-1.5 block text-xs leading-relaxed text-muted-fg">{hint}</span>}
      {error && <span className="mt-1.5 block text-xs text-destructive">{error}</span>}
    </Tag>
  );
}

const INPUT =
  'block w-full rounded-sm border-0 bg-surface text-fg ring-1 ring-inset ring-border-strong placeholder:text-faint transition duration-150 hover:ring-fg/30 focus:ring-2 focus:ring-accent focus:outline-none disabled:bg-muted disabled:text-muted-fg disabled:hover:ring-border-strong dark:bg-white/4';

export function Input({ leading, trailing, big, className, ...props }: InputHTMLAttributes<HTMLInputElement> & { leading?: ReactNode; trailing?: ReactNode; big?: boolean }) {
  const field = <input {...props} className={cx(INPUT, big ? 'h-12 text-base' : 'h-10 text-sm', leading ? 'pl-10' : 'pl-3.5', trailing ? 'pr-10' : 'pr-3.5', className)} />;
  if (!leading && !trailing) return field;
  return (
    <span className="relative block">
      {leading && <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-muted-fg">{leading}</span>}
      {field}
      {trailing && <span className="absolute inset-y-0 right-3 flex items-center text-muted-fg">{trailing}</span>}
    </span>
  );
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={cx(INPUT, 'min-h-20 resize-y px-3.5 py-2.5 text-sm leading-relaxed', props.className)} />;
}

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <span className="relative block">
      <select {...props} className={cx(INPUT, 'h-10 appearance-none pl-3.5 pr-9 text-sm', className)}>
        {children}
      </select>
      <Icon name="chevronDown" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-fg" />
    </span>
  );
}

/** A number with - and + steppers, for minutes, attempts, counts. */
export function NumberField({
  value,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  suffix,
  className,
  'aria-label': ariaLabel,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  className?: string;
  'aria-label'?: string;
}) {
  const clamp = (n: number) => Math.max(min, Math.min(max, n));
  return (
    <span className={cx('inline-flex h-10 w-full items-stretch overflow-hidden rounded-sm bg-surface ring-1 ring-inset ring-border-strong transition focus-within:ring-2 focus-within:ring-accent dark:bg-white/4', className)}>
      <button type="button" className="w-9 shrink-0 text-muted-fg hover:bg-muted hover:text-fg disabled:opacity-30" onClick={() => onChange(clamp(value - step))} disabled={value <= min} aria-label="Less">
        <Icon name="minus" className="mx-auto h-4 w-4" />
      </button>
      <input
        type="number"
        aria-label={ariaLabel}
        className="min-w-0 flex-1 border-0 bg-transparent text-center text-sm font-medium tabular text-fg focus:outline-none"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(clamp(Number(e.target.value) || min))}
      />
      {suffix && <span className="flex items-center pr-2 text-xs text-muted-fg">{suffix}</span>}
      <button type="button" className="w-9 shrink-0 text-muted-fg hover:bg-muted hover:text-fg disabled:opacity-30" onClick={() => onChange(clamp(value + step))} disabled={value >= max} aria-label="More">
        <Icon name="plus" className="mx-auto h-4 w-4" />
      </button>
    </span>
  );
}

export function Checkbox({ checked, onChange, children, hint, disabled, className }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode; hint?: ReactNode; disabled?: boolean; className?: string }) {
  return (
    <label className={cx('flex cursor-pointer items-start gap-3', disabled && 'cursor-not-allowed opacity-60', className)}>
      <span className="relative mt-0.5 inline-flex h-[18px] w-[18px] shrink-0">
        <input type="checkbox" className="peer sr-only" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <span className="absolute inset-0 rounded-[5px] bg-surface ring-1 ring-inset ring-border-strong transition peer-checked:bg-brand-gradient peer-checked:ring-transparent peer-focus-visible:ring-2 peer-focus-visible:ring-accent dark:bg-white/5" />
        <Icon name="check" className="relative m-auto h-3 w-3 text-white opacity-0 transition peer-checked:opacity-100" strokeWidth={3} />
      </span>
      <span className="min-w-0 text-sm">
        <span className="font-medium text-fg">{children}</span>
        {hint && <span className="mt-0.5 block text-xs leading-relaxed text-muted-fg">{hint}</span>}
      </span>
    </label>
  );
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx('relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition duration-200 disabled:opacity-50', checked ? 'bg-brand-gradient shadow-brand' : 'bg-border-strong')}
    >
      <span className={cx('inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200', checked ? 'translate-x-[22px]' : 'translate-x-0.5')} />
    </button>
  );
}

/** Choose one of a few options shown as cards: outreach mode, voice engine, how to draft. */
export function Options<T extends string>({
  value,
  options,
  onChange,
  columns = 3,
  label,
  compact,
}: {
  value: T;
  options: Array<{ value: T; label: ReactNode; hint?: ReactNode; icon?: IconName; badge?: ReactNode }>;
  onChange: (v: T) => void;
  columns?: 1 | 2 | 3 | 4;
  label: string;
  compact?: boolean;
}) {
  const cols = { 1: 'grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-2 xl:grid-cols-4' }[columns];
  return (
    <div role="radiogroup" aria-label={label} className={cx('grid gap-2.5', cols)}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={cx(
              'group relative flex items-start gap-3 rounded-md text-left ring-1 ring-inset transition duration-150',
              compact ? 'px-3.5 py-3' : 'p-4',
              on ? 'bg-accent-soft ring-2 ring-accent' : 'bg-surface ring-border-strong hover:bg-surface-2 hover:ring-fg/30 dark:bg-white/4',
            )}
          >
            {o.icon && (
              <span className={cx('mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] transition', on ? 'bg-brand-gradient text-white shadow-brand' : 'bg-muted text-muted-fg group-hover:text-fg')}>
                <Icon name={o.icon} className="h-4 w-4" />
              </span>
            )}
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2 text-sm font-semibold text-fg">
                {o.label}
                {o.badge}
              </span>
              {o.hint && <span className="mt-0.5 block text-xs leading-relaxed text-muted-fg">{o.hint}</span>}
            </span>
            <span className={cx('mt-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full ring-1 ring-inset transition', on ? 'bg-accent ring-accent' : 'ring-border-strong')} aria-hidden>
              {on && <Icon name="check" className="h-2.5 w-2.5 text-white" strokeWidth={3.5} />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** A small segmented control (radio group semantics). */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  size = 'sm',
  className,
}: {
  value: T;
  options: Array<{ value: T; label: ReactNode; icon?: IconName }>;
  onChange: (v: T) => void;
  label: string;
  size?: 'sm' | 'md';
  className?: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className={cx('inline-flex shrink-0 rounded-sm bg-muted p-1 ring-1 ring-inset ring-border', className)}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={cx(
              'inline-flex items-center gap-1.5 rounded-[7px] font-semibold transition duration-150',
              size === 'sm' ? 'px-3 py-1 text-xs' : 'px-4 py-1.5 text-sm',
              on ? 'bg-surface text-fg shadow-[0_1px_3px_rgba(23,20,31,0.12)] dark:bg-white/10' : 'text-muted-fg hover:text-fg',
            )}
          >
            {o.icon && <Icon name={o.icon} className="h-3.5 w-3.5" />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Underlined tabs with optional counts, for views inside a page. */
export function Tabs<T extends string>({ value, options, onChange, label }: { value: T; options: Array<{ value: T; label: ReactNode; count?: number }>; onChange: (v: T) => void; label: string }) {
  return (
    <div role="tablist" aria-label={label} className="flex gap-1 overflow-x-auto border-b border-border">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(o.value)}
            className={cx(
              '-mb-px inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition',
              on ? 'border-accent text-fg' : 'border-transparent text-muted-fg hover:text-fg',
            )}
          >
            {o.label}
            {o.count !== undefined && <span className={cx('rounded-full px-1.5 py-px font-mono text-[10px]', on ? 'bg-accent-soft text-accent' : 'bg-muted text-muted-fg')}>{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ---- data display ----------------------------------------------------------------------------

export function Stat({ label, value, note, accent, icon, className }: { label: ReactNode; value: ReactNode; note?: ReactNode; accent?: boolean; icon?: IconName; className?: string }) {
  return (
    <div className={cx('surface relative min-w-0 overflow-hidden rounded-lg p-5', className)}>
      {accent && <span className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-brand-gradient opacity-[0.08] blur-2xl" aria-hidden />}
      <div className="flex items-center justify-between gap-2">
        <p className="eyebrow">{label}</p>
        {icon && <Icon name={icon} className="h-4 w-4 text-faint" />}
      </div>
      <p className={cx('display mt-3 text-[34px] font-medium leading-none tabular', accent && 'text-brand-gradient')}>{value}</p>
      {note && <p className="mt-2.5 text-xs text-muted-fg">{note}</p>}
    </div>
  );
}

export function ProgressBar({ value, max = 100, tone = 'brand', size = 'md', className }: { value: number; max?: number; tone?: 'brand' | 'success' | 'warning' | 'danger' | 'auto'; size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const t = tone === 'auto' ? (pct >= 70 ? 'success' : pct >= 55 ? 'warning' : 'danger') : tone;
  const fill = { brand: 'bg-brand-gradient', success: 'bg-success', warning: 'bg-warning', danger: 'bg-destructive' }[t];
  const h = { sm: 'h-1.5', md: 'h-2', lg: 'h-3' }[size];
  return (
    <div className={cx('w-full overflow-hidden rounded-full bg-muted', h, className)} role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
      <div className={cx('h-full rounded-full transition-[width] duration-500', fill)} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Kept for callers that still use the old name. */
export const ScoreBar = ({ value, max = 100, className }: { value: number; max?: number; className?: string }) => <ProgressBar value={value} max={max} tone="auto" className={className} />;

/** A progress ring in the brand gradient, with whatever goes in the middle. */
export function Ring({ value, max = 100, size = 96, stroke = 8, tone = 'brand', children, className, label }: { value: number; max?: number; size?: number; stroke?: number; tone?: 'brand' | 'success' | 'warning' | 'danger' | 'auto'; children?: ReactNode; className?: string; label?: string }) {
  const id = useId();
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, value / max));
  const t = tone === 'auto' ? (pct * 100 >= 70 ? 'success' : pct * 100 >= 55 ? 'warning' : 'danger') : tone;
  const stroke_ = t === 'brand' ? `url(#${id})` : `var(--${t === 'danger' ? 'destructive' : t})`;
  return (
    <div className={cx('relative inline-flex shrink-0 items-center justify-center', className)} style={{ width: size, height: size }} role="img" aria-label={label ?? `${Math.round(pct * 100)}%`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="absolute inset-0">
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#d6217f" />
            <stop offset="1" stopColor="#7e2ea6" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--muted)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={stroke_}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 700ms var(--ease-out-soft)' }}
        />
      </svg>
      <div className="relative flex flex-col items-center justify-center">{children}</div>
    </div>
  );
}

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #d6217f, #7e2ea6)',
  'linear-gradient(135deg, #7e2ea6, #2457c5)',
  'linear-gradient(135deg, #0ea5e9, #6366f1)',
  'linear-gradient(135deg, #f59e0b, #d6217f)',
  'linear-gradient(135deg, #10b981, #0ea5e9)',
  'linear-gradient(135deg, #a8279a, #f59e0b)',
];

export function initialsOf(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')).toUpperCase() || '?';
}

export function Avatar({ name, size = 'md', className, ring }: { name: string | null | undefined; size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'; className?: string; ring?: boolean }) {
  const hash = [...(name ?? '')].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) >>> 0, 7);
  const sizes = { xs: 'h-6 w-6 text-[9px]', sm: 'h-8 w-8 text-[11px]', md: 'h-10 w-10 text-xs', lg: 'h-14 w-14 text-base', xl: 'h-20 w-20 text-xl' };
  return (
    <span
      className={cx('inline-flex shrink-0 select-none items-center justify-center rounded-full font-semibold tracking-wide text-white', sizes[size], ring && 'ring-2 ring-surface shadow-card', className)}
      style={{ background: AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length] }}
      aria-hidden
    >
      {initialsOf(name)}
    </span>
  );
}

/** Table primitives: a wrapper with the ring, and class constants for cells. */
export function Table({ children, className, minWidth }: { children: ReactNode; className?: string; minWidth?: string }) {
  return (
    <div className={cx('surface scrollbar-thin overflow-x-auto rounded-lg', className)}>
      <table className="w-full text-sm" style={minWidth ? { minWidth } : undefined}>
        {children}
      </table>
    </div>
  );
}
export const TH = 'px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-fg';
export const TD = 'px-4 py-3.5 align-top';
export const TR = 'border-t border-border transition hover:bg-surface-2';

// ---- page furniture --------------------------------------------------------------------------

export function PageHeader({
  title,
  subtitle,
  actions,
  back,
  eyebrow,
  meta,
  aside,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  back?: { to: string; label: string };
  eyebrow?: ReactNode;
  /** Small facts under the title: location, mode, length. */
  meta?: ReactNode;
  /** Something beside the title, like a status pill or an avatar. */
  aside?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('fade-up mb-8', className)}>
      {back && (
        <Link to={back.to} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-muted-fg transition hover:text-accent">
          <Icon name="arrowLeft" className="h-4 w-4" />
          {back.label}
        </Link>
      )}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <div className="min-w-0">
          {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="display text-[32px] font-medium leading-[1.1] text-fg sm:text-[38px]">{title}</h1>
            {aside}
          </div>
          {subtitle && <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-muted-fg">{subtitle}</p>}
          {meta && <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-fg">{meta}</div>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function Meta({ icon, children }: { icon: IconName; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon name={icon} className="h-3.5 w-3.5 text-faint" />
      {children}
    </span>
  );
}

/** Steps across the top of a flow: the new-job wizard. Earlier steps can be clicked. */
export function Stepper({ steps, current, onStep }: { steps: Array<{ label: string; hint?: string }>; current: number; onStep?: (i: number) => void }) {
  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-2" aria-label="Steps">
      {steps.map((s, i) => {
        const done = i < current;
        const on = i === current;
        const clickable = done && onStep;
        return (
          <li key={s.label} className="flex items-center gap-2">
            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onStep(i)}
              className={cx('flex items-center gap-2.5 rounded-full py-1 pl-1 pr-3 text-sm transition', clickable ? 'hover:bg-muted' : 'cursor-default')}
              aria-current={on ? 'step' : undefined}
            >
              <span
                className={cx(
                  'inline-flex h-7 w-7 items-center justify-center rounded-full font-mono text-[11px] font-semibold ring-1 ring-inset transition',
                  on ? 'bg-brand-gradient text-white shadow-brand ring-transparent' : done ? 'bg-accent-soft text-accent ring-accent/30' : 'bg-surface text-faint ring-border-strong',
                )}
              >
                {done ? <Icon name="check" className="h-3.5 w-3.5" strokeWidth={2.5} /> : i + 1}
              </span>
              <span className={cx('font-medium', on ? 'text-fg' : done ? 'text-fg-2' : 'text-faint')}>{s.label}</span>
              {s.hint && on && <span className="hidden text-muted-fg sm:inline">· {s.hint}</span>}
            </button>
            {i < steps.length - 1 && <span className={cx('h-px w-6 sm:w-10', done ? 'bg-accent/40' : 'bg-border-strong')} aria-hidden />}
          </li>
        );
      })}
    </ol>
  );
}

/** A floating bar at the bottom while something is selected. */
export function SelectionBar({ count, noun = 'selected', onClear, children }: { count: number; noun?: string; onClear: () => void; children: ReactNode }) {
  if (count === 0) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-5 z-40 flex justify-center px-4 lg:pl-[264px]">
      <div className="scale-in pointer-events-auto flex max-w-full flex-wrap items-center gap-2 rounded-xl bg-ink px-3 py-2 text-white shadow-pop ring-1 ring-white/10">
        <span className="flex items-center gap-2 pl-1 pr-2 text-sm">
          <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-brand-gradient px-1.5 font-mono text-[11px] font-semibold">{count}</span>
          <span className="text-white/80">{noun}</span>
        </span>
        <span className="h-5 w-px bg-white/15" aria-hidden />
        {children}
        <button type="button" onClick={onClear} className="ml-1 inline-flex h-8 w-8 items-center justify-center rounded-[9px] text-white/60 hover:bg-white/10 hover:text-white" aria-label="Clear selection">
          <Icon name="x" className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/** Buttons for inside a SelectionBar (dark background). */
export function BarButton({ children, onClick, danger, primary, icon }: { children: ReactNode; onClick: () => void; danger?: boolean; primary?: boolean; icon?: IconName }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'inline-flex h-8 items-center gap-1.5 rounded-[9px] px-3 text-xs font-semibold transition',
        primary ? 'bg-brand-gradient text-white shadow-brand hover:brightness-110' : danger ? 'text-[#fda4b4] hover:bg-white/10' : 'text-white/85 hover:bg-white/10 hover:text-white',
      )}
    >
      {icon && <Icon name={icon} className="h-3.5 w-3.5" />}
      {children}
    </button>
  );
}

// ---- overlays --------------------------------------------------------------------------------

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  const widths = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-3xl', xl: 'max-w-5xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/60 p-4 pt-[7vh] backdrop-blur-sm" onMouseDown={onClose}>
      <div role="dialog" aria-modal="true" className={cx('scale-in w-full overflow-hidden rounded-xl bg-surface-raised shadow-pop ring-1 ring-border', widths[size])} onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 px-6 pt-5">
          <div className="min-w-0">
            <h3 className="display text-xl font-medium text-fg">{title}</h3>
            {description && <p className="mt-1 text-sm text-muted-fg">{description}</p>}
          </div>
          <IconButton label="Close" icon="x" size="sm" onClick={onClose} className="-mr-2 -mt-1" />
        </div>
        <div className="scrollbar-thin max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-border bg-surface-2 px-6 py-4">{footer}</div>}
      </div>
    </div>
  );
}

/** A small menu behind a button: the less common actions. */
export function Menu({
  label,
  icon = 'more',
  items,
  align = 'right',
  size = 'md',
}: {
  label: string;
  icon?: IconName;
  items: Array<{ label: string; icon?: IconName; onSelect: () => void; danger?: boolean; disabled?: boolean; hint?: string } | 'divider'>;
  align?: 'left' | 'right';
  size?: 'sm' | 'md';
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative inline-block">
      <IconButton label={label} icon={icon} size={size} variant="secondary" onClick={() => setOpen(!open)} aria-haspopup="menu" aria-expanded={open} />
      {open && (
        <div role="menu" className={cx('scale-in absolute z-40 mt-1.5 min-w-56 rounded-md bg-surface-raised p-1.5 shadow-pop ring-1 ring-border', align === 'right' ? 'right-0' : 'left-0')}>
          {items.map((it, i) =>
            it === 'divider' ? (
              <div key={i} className="my-1.5 h-px bg-border" role="separator" />
            ) : (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                disabled={it.disabled}
                onClick={() => {
                  setOpen(false);
                  it.onSelect();
                }}
                className={cx(
                  'flex w-full items-start gap-2.5 rounded-[8px] px-2.5 py-2 text-left text-sm transition disabled:opacity-40',
                  it.danger ? 'text-destructive hover:bg-destructive-soft' : 'text-fg hover:bg-muted',
                )}
              >
                {it.icon && <Icon name={it.icon} className="mt-0.5 h-4 w-4 shrink-0 opacity-70" />}
                <span className="min-w-0">
                  <span className="block font-medium">{it.label}</span>
                  {it.hint && <span className="block text-xs text-muted-fg">{it.hint}</span>}
                </span>
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}

/** The gradient "E" mark. */
export function LogoMark({ className = 'h-8 w-8' }: { className?: string }) {
  return (
    <span className={cx('relative inline-flex items-center justify-center overflow-hidden rounded-[10px] bg-brand-gradient font-display text-lg font-semibold text-white shadow-brand', className)} aria-hidden>
      <span className="absolute -right-2 -top-2 h-5 w-5 rounded-full bg-white/25 blur-[6px]" />
      <span className="relative">E</span>
    </span>
  );
}

/** Copies text; shows "Copied" for a moment. */
export function CopyButton({ text, children = 'Copy link', size = 'xs', variant = 'ghost', icon = 'copy' }: { text: string; children?: ReactNode; size?: Size; variant?: Variant; icon?: IconName }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size={size}
      variant={variant}
      icon={copied ? 'check' : icon}
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? 'Copied' : children}
    </Button>
  );
}
